import { useEffect, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  DatabaseBackup,
  ExternalLink,
  FileSignature,
  GitBranch,
  BookOpen,
  ShieldAlert,
  Plus,
  Save,
  Server,
  Shield,
  Wrench
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { formatDate, get, localDateTime, post, put, toIso } from '../lib/api';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../components/StatusBadge';

const roleNames: Record<string, string> = {
  it_engineer: 'Инженер по ИТ',
  system_administrator: 'Системный администратор',
  low_voltage_engineer: 'Инженер по слаботочным системам',
  automation_engineer: 'Инженер КИПиА',
  kipia_engineer: 'Инженер КИПиА',
  multimedia_engineer: 'Инженер по мультимедиа',
  security_specialist: 'Специалист по ИБ'
};

const licenseLabels: Record<string, string> = {
  unknown: 'Не оценено',
  licensed: 'Лицензия имеется',
  subscription: 'Подписка',
  trial: 'Пробная лицензия',
  not_required: 'Не требуется',
  missing: 'Отсутствует',
  expired: 'Истекла'
};

const roleLabel = (value?: string | null) => value ? (roleNames[value] || value) : '—';

function emptyIncident(responsible = '') {
  return {
    title: '',
    description: '',
    priority: 'p3',
    status: 'new',
    source: 'manual',
    assignee: responsible,
    impact: '',
    resolution: '',
    started_at: localDateTime(new Date().toISOString()),
    acknowledged_at: '',
    resolved_at: ''
  };
}

export function SystemPage() {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [form, setForm] = useState<any>(null);
  const [incidentForm, setIncidentForm] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [incidentSaving, setIncidentSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [incidentError, setIncidentError] = useState('');

  const load = () => get<any>(`/api/systems/${id}`).then(result => {
    setData(result);
    setForm({
      ...result.system,
      monitoring_enabled: Boolean(result.system.monitoring_enabled),
      backup_required: Boolean(result.system.backup_required),
      last_check_at: localDateTime(result.system.last_check_at),
      next_maintenance_at: localDateTime(result.system.next_maintenance_at),
      last_update_at: localDateTime(result.system.last_update_at),
      version_checked_at: localDateTime(result.system.version_checked_at),
      license_expires_at: localDateTime(result.system.license_expires_at)
    });
  });

  useEffect(() => {
    void load();
  }, [id]);

  if (!data || !form) return <div className="skeleton-page" />;

  const set = (key: string, value: unknown) => setForm({ ...form, [key]: value });

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await put(`/api/systems/${id}`, {
        ...form,
        health_score: form.health_score === '' ? null : Number(form.health_score),
        availability_30d: form.availability_30d === '' ? null : Number(form.availability_30d),
        last_check_at: toIso(form.last_check_at),
        next_maintenance_at: toIso(form.next_maintenance_at),
        last_update_at: toIso(form.last_update_at),
        version_checked_at: toIso(form.version_checked_at),
        license_expires_at: toIso(form.license_expires_at)
      });
      setMessage('Изменения сохранены');
      await load();
    } catch (error: any) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const createIncident = async (event: React.FormEvent) => {
    event.preventDefault();
    setIncidentSaving(true);
    setIncidentError('');
    try {
      await post('/api/incidents', {
        ...incidentForm,
        system_id: id,
        started_at: toIso(incidentForm.started_at),
        acknowledged_at: toIso(incidentForm.acknowledged_at),
        resolved_at: toIso(incidentForm.resolved_at)
      });
      setIncidentForm(null);
      await load();
    } catch (error: any) {
      setIncidentError(error.message);
    } finally {
      setIncidentSaving(false);
    }
  };

  const system = data.system;
  const catalog = system.full?.catalog || {};
  const licenseStatus = form.license_status || 'unknown';

  return <>
    <div className="detail-nav">
      <Link to="/systems"><ArrowLeft size={17} />К реестру</Link>
      <div>
        <StatusBadge value={system.status} />
        <span className={`criticality crit-${system.criticality}`}>{system.criticality}</span>
      </div>
    </div>

    <section className="system-hero">
      <div className="system-big-icon"><Server /></div>
      <div>
        <div className="eyebrow">{system.code} · направление {system.direction_number}</div>
        <h2>{system.name}</h2>
        <p>{system.direction_name}</p>
      </div>
      <div className="system-score">
        <span>Доступность 30 дней</span>
        <strong>{system.availability_30d === null ? '—' : `${system.availability_30d}%`}</strong>
        <small>Заполняется вручную</small>
      </div>
    </section>

    <div className="tabs-row">
      <a href="#state">Состояние</a>
      <a href="#card">Техническая карточка</a>
      <a href="#control">Контроль</a>
      <a href="#contracts">Договоры и лицензии</a>
      <a href="#relationships">Связи</a>
      <a href="#problems">Проблемы и риски</a>
      <a href="#history">История</a>
    </div>

    <section className="edit-grid" id="state">
      <article className="panel span-2">
        <div className="panel-head">
          <div>
            <div className="eyebrow">Фактическая оценка</div>
            <h3>Состояние и влияние</h3>
          </div>
          <button className="primary" onClick={() => void save()} disabled={saving}>
            <Save size={17} />{saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
        {message && <div className="save-message">{message}</div>}
        <div className="form-grid">
          <label>Статус
            <select value={form.status} onChange={event => set('status', event.target.value)}>
              <option value="not_configured">Не настроено</option>
              <option value="operational">Работает штатно</option>
              <option value="degraded">С отклонениями</option>
              <option value="partial_outage">Частичный отказ</option>
              <option value="outage">Недоступно</option>
              <option value="maintenance">Техническое обслуживание</option>
              <option value="decommissioned">Выведено из эксплуатации</option>
            </select>
          </label>
          <label>Критичность
            <select value={form.criticality} onChange={event => set('criticality', event.target.value)}>
              <option value="critical">Критическая</option>
              <option value="high">Высокая</option>
              <option value="medium">Средняя</option>
              <option value="low">Низкая</option>
            </select>
          </label>
          <label>Оценка состояния, %
            <input type="number" min="0" max="100" value={form.health_score ?? ''} onChange={event => set('health_score', event.target.value)} />
          </label>
          <label>Доступность за 30 дней, %
            <input type="number" step="0.01" min="0" max="100" value={form.availability_30d ?? ''} onChange={event => set('availability_30d', event.target.value)} />
          </label>
          <label className="span-2">Проблемы и отклонения
            <textarea rows={4} value={form.problem_summary || ''} onChange={event => set('problem_summary', event.target.value)} placeholder="Фактические неисправности, симптомы и причины" />
          </label>
          <label className="span-2">Влияние и риск
            <textarea rows={3} value={form.impact_summary || ''} onChange={event => set('impact_summary', event.target.value)} placeholder="Какие процессы и пользователи затронуты" />
          </label>
          <label className="span-2">Необходимые меры
            <textarea rows={3} value={form.measures || ''} onChange={event => set('measures', event.target.value)} placeholder="Что требуется сделать, срок и ожидаемый результат" />
          </label>
        </div>
      </article>

      <article className="panel">
        <div className="panel-head">
          <div>
            <div className="eyebrow">Ответственность</div>
            <h3>Владелец системы</h3>
          </div>
          <Shield />
        </div>
        <label>Основной ответственный
          <input value={form.responsible || ''} onChange={event => set('responsible', event.target.value)} />
        </label>
        <div className="info-list">
          <div><span>Прикладной администратор</span><strong>{roleLabel(catalog.owners?.application_admin_role)}</strong></div>
          <div><span>Владелец платформы</span><strong>{roleLabel(catalog.owners?.platform_owner_role)}</strong></div>
          <div><span>ИБ</span><strong>{roleLabel(catalog.owners?.security_owner_role)}</strong></div>
        </div>
      </article>
    </section>

    <section className="edit-grid" id="card">
      <article className="panel span-2">
        <div className="panel-head">
          <div>
            <div className="eyebrow">Паспорт</div>
            <h3>Техническая карточка</h3>
          </div>
        </div>
        <div className="form-grid">
          <label>Производитель
            <input value={form.vendor || ''} onChange={event => set('vendor', event.target.value)} />
          </label>
          <label>Продукт
            <input value={form.product || ''} onChange={event => set('product', event.target.value)} />
          </label>
          <label>Текущая версия
            <input value={form.version || ''} onChange={event => set('version', event.target.value)} placeholder="Установленная версия" />
          </label>
          <label>Доступная версия
            <input value={form.available_version || ''} onChange={event => set('available_version', event.target.value)} placeholder="Будет заполняться API" />
          </label>
          <label>Статус обновления
            <select value={form.update_status || 'unknown'} onChange={event => set('update_status', event.target.value)}>
              <option value="unknown">Не проверено</option>
              <option value="current">Актуальная версия</option>
              <option value="update_available">Доступно обновление</option>
              <option value="planned">Обновление запланировано</option>
              <option value="blocked">Обновление заблокировано</option>
              <option value="unsupported">Версия не поддерживается</option>
            </select>
          </label>
          <label>Проверка версии
            <input type="datetime-local" value={form.version_checked_at || ''} onChange={event => set('version_checked_at', event.target.value)} />
          </label>
          <label className="span-2">Источник проверки версии / API
            <input value={form.version_source || ''} onChange={event => set('version_source', event.target.value)} placeholder="URL API, страница релизов или источник данных" />
          </label>
          <label>Последнее установленное обновление
            <input type="datetime-local" value={form.last_update_at || ''} onChange={event => set('last_update_at', event.target.value)} />
          </label>
          <label>Наличие лицензии
            <select value={licenseStatus} onChange={event => set('license_status', event.target.value)}>
              {Object.entries(licenseLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Лицензия действует до
            <input type="datetime-local" value={form.license_expires_at || ''} onChange={event => set('license_expires_at', event.target.value)} disabled={licenseStatus === 'not_required'} />
          </label>
          <label className="span-2">Сведения о лицензии
            <textarea rows={2} value={form.license_notes || ''} onChange={event => set('license_notes', event.target.value)} placeholder="Тип, номер, количество, договор или особенности лицензирования" />
          </label>
          <label>Местоположение
            <input value={form.location || ''} onChange={event => set('location', event.target.value)} />
          </label>
          <label>URL / адреса
            <input value={form.url || ''} onChange={event => set('url', event.target.value)} />
          </label>
          <label className="span-2">Примечания
            <textarea rows={4} value={form.notes || ''} onChange={event => set('notes', event.target.value)} />
          </label>
        </div>
        {catalog.equipment_groups?.length > 0 && <div className="tag-block">
          <span>Состав и группы оборудования</span>
          <div>{catalog.equipment_groups.map((item: string) => <em key={item}>{item}</em>)}</div>
        </div>}
      </article>

      <article className="panel" id="control">
        <div className="panel-head">
          <div>
            <div className="eyebrow">Контроль</div>
            <h3>Мониторинг и бэкап</h3>
          </div>
          <DatabaseBackup />
        </div>
        <label className="switch-row">
          <span><strong>Мониторинг настроен</strong><small>Учитывать в сводке</small></span>
          <input type="checkbox" checked={form.monitoring_enabled} onChange={event => set('monitoring_enabled', event.target.checked)} />
        </label>
        <label className="switch-row">
          <span><strong>Резервирование требуется</strong><small>Контроль копий</small></span>
          <input type="checkbox" checked={form.backup_required} onChange={event => set('backup_required', event.target.checked)} />
        </label>
        <label>Состояние резервирования
          <select value={form.backup_state || 'unknown'} onChange={event => set('backup_state', event.target.value)}>
            <option value="unknown">Не оценено</option>
            <option value="ok">Без нарушений</option>
            <option value="warning">Есть отклонения</option>
            <option value="failed">Нарушено</option>
            <option value="not_required">Не требуется</option>
          </select>
        </label>
        <label>Последняя проверка
          <input type="datetime-local" value={form.last_check_at || ''} onChange={event => set('last_check_at', event.target.value)} />
        </label>
        <label>Следующее ТО
          <input type="datetime-local" value={form.next_maintenance_at || ''} onChange={event => set('next_maintenance_at', event.target.value)} />
        </label>
      </article>
    </section>

    <section className="portfolio-system-grid" id="contracts">
      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Сопровождение</div><h3>Договоры на обслуживание</h3></div><Link className="mini-add" to="/contracts"><FileSignature size={15}/>Открыть реестр</Link></div>
        {data.contracts?.length ? <div className="mini-history">{data.contracts.slice(0,5).map((item:any)=><div key={item.id}><StatusBadge value={item.status}/><div><strong>{item.title}</strong><small>{item.contract_number ? `№ ${item.contract_number} · ` : ''}до {formatDate(item.end_at,false)}</small></div></div>)}</div> : <div className="empty small">Договоров нет</div>}
      </article>
      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Права использования</div><h3>Лицензии</h3></div><Link className="mini-add" to="/contracts"><FileSignature size={15}/>Открыть реестр</Link></div>
        {data.licenses?.length ? <div className="mini-history">{data.licenses.slice(0,5).map((item:any)=><div key={item.id}><StatusBadge value={item.status}/><div><strong>{item.title}</strong><small>{item.license_type || 'Тип не указан'} · {item.quantity ?? '—'} шт. · до {formatDate(item.expires_at,false)}</small></div></div>)}</div> : <div className="empty small">Лицензий нет</div>}
      </article>
    </section>

    <section className="portfolio-system-grid" id="relationships">
      <article className="panel span-2">
        <div className="panel-head"><div><div className="eyebrow">CMDB</div><h3>Связи и зависимости</h3></div><Link className="mini-add" to="/relationships"><GitBranch size={15}/>Управлять связями</Link></div>
        {data.relationships?.length ? <div className="relationship-mini-list">{data.relationships.slice(0,8).map((item:any)=>{const outgoing=item.source_system_id===system.id;return <div key={item.id}><span>{outgoing?'Зависит / связан с':'Используется системой'}</span><strong>{outgoing?`${item.target_system_code} · ${item.target_system_name}`:`${item.source_system_code} · ${item.source_system_name}`}</strong><small>{item.title}</small></div>})}</div> : <div className="empty small">Связи не заведены</div>}
      </article>
    </section>

    <section className="portfolio-system-grid" id="problems">
      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Повторяющиеся причины</div><h3>Проблемы</h3></div><Link className="mini-add" to="/problems"><ShieldAlert size={15}/>Открыть</Link></div>
        {data.problems?.length ? <div className="mini-history">{data.problems.slice(0,5).map((item:any)=><div key={item.id}><StatusBadge value={item.status}/><div><strong>{item.title}</strong><small>{item.root_cause || 'Причина уточняется'}</small></div></div>)}</div> : <div className="empty small">Проблем нет</div>}
      </article>
      <article className="panel">
        <div className="panel-head"><div><div className="eyebrow">Управленческий контроль</div><h3>Риски</h3></div><Link className="mini-add" to="/risks"><ShieldAlert size={15}/>Открыть</Link></div>
        {data.risks?.length ? <div className="mini-history">{data.risks.slice(0,5).map((item:any)=><div key={item.id}><span className={`risk-score score-${item.risk_score>=15?'high':item.risk_score>=8?'medium':'low'}`}>{item.risk_score}</span><div><strong>{item.title}</strong><small>{item.mitigation || 'Меры не заполнены'}</small></div></div>)}</div> : <div className="empty small">Риски не зарегистрированы</div>}
      </article>
      <article className="panel span-2">
        <div className="panel-head"><div><div className="eyebrow">Инструкции</div><h3>База знаний</h3></div><Link className="mini-add" to="/knowledge"><BookOpen size={15}/>Открыть</Link></div>
        {data.knowledge?.length ? <div className="knowledge-mini-grid">{data.knowledge.slice(0,6).map((item:any)=><div key={item.id}><StatusBadge value={item.status}/><strong>{item.title}</strong><small>{item.solution || item.description || 'Содержание не заполнено'}</small></div>)}</div> : <div className="empty small">Статей нет</div>}
      </article>
    </section>

    <section className="history-grid" id="history">
      <History
        title="Инциденты"
        icon={<Shield />}
        items={data.incidents}
        empty="Инцидентов нет"
        action={<button className="mini-add" onClick={() => setIncidentForm(emptyIncident(system.responsible || ''))}><Plus size={15} />Добавить</button>}
      />
      <History title="ТО и ППР" icon={<Wrench />} items={data.maintenance} empty="Работы не запланированы" />
      <History title="Изменения" icon={<ExternalLink />} items={data.changes} empty="Изменений нет" />
    </section>

    {incidentForm && <Modal title={`Новый инцидент · ${system.name}`} onClose={() => setIncidentForm(null)} wide>
      <form className="form-grid" onSubmit={createIncident}>
        <label className="span-2">Система
          <input value={`${system.code} · ${system.name}`} disabled />
        </label>
        <label className="span-2">Название
          <input required value={incidentForm.title} onChange={event => setIncidentForm({ ...incidentForm, title: event.target.value })} />
        </label>
        <label className="span-2">Описание
          <textarea rows={4} value={incidentForm.description} onChange={event => setIncidentForm({ ...incidentForm, description: event.target.value })} />
        </label>
        <label>Статус
          <select value={incidentForm.status} onChange={event => setIncidentForm({ ...incidentForm, status: event.target.value })}>
            <option value="new">Новый</option>
            <option value="acknowledged">Принят в работу</option>
            <option value="diagnosis">Диагностика</option>
            <option value="workaround">Временное решение</option>
            <option value="resolved">Устранён</option>
            <option value="closed">Закрыт</option>
          </select>
        </label>
        <label>Приоритет
          <select value={incidentForm.priority} onChange={event => setIncidentForm({ ...incidentForm, priority: event.target.value })}>
            <option value="p1">P1 — критический</option>
            <option value="p2">P2 — высокий</option>
            <option value="p3">P3 — средний</option>
            <option value="p4">P4 — низкий</option>
          </select>
        </label>
        <label>Ответственный
          <input value={incidentForm.assignee} onChange={event => setIncidentForm({ ...incidentForm, assignee: event.target.value })} />
        </label>
        <label>Начало
          <input type="datetime-local" required value={incidentForm.started_at} onChange={event => setIncidentForm({ ...incidentForm, started_at: event.target.value })} />
        </label>
        <label className="span-2">Влияние
          <textarea rows={3} value={incidentForm.impact} onChange={event => setIncidentForm({ ...incidentForm, impact: event.target.value })} />
        </label>
        <label className="span-2">Решение
          <textarea rows={3} value={incidentForm.resolution} onChange={event => setIncidentForm({ ...incidentForm, resolution: event.target.value })} />
        </label>
        {incidentError && <div className="form-error span-2">{incidentError}</div>}
        <div className="modal-actions span-2">
          <button type="button" className="secondary" onClick={() => setIncidentForm(null)}>Отмена</button>
          <button className="primary" disabled={incidentSaving}>{incidentSaving ? 'Сохранение…' : 'Создать инцидент'}</button>
        </div>
      </form>
    </Modal>}
  </>;
}

function History({ title, icon, items, empty, action }: {
  title: string;
  icon: ReactNode;
  items: any[];
  empty: string;
  action?: ReactNode;
}) {
  return <article className="panel">
    <div className="panel-head">
      <h3>{title}</h3>
      <div className="panel-head-actions">{action}{icon}</div>
    </div>
    {items.length
      ? <div className="mini-history">{items.slice(0, 6).map(item => <div key={item.id}>
          <StatusBadge value={item.status} />
          <div>
            <strong>{item.title}</strong>
            <small>{formatDate(item.started_at || item.scheduled_start || item.planned_at || item.created_at)}</small>
          </div>
        </div>)}</div>
      : <div className="empty small">{empty}</div>}
  </article>;
}
